import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { Guide, GuideCompletion, NewsPost, UpdateNote, Resource, DocumentTemplate } from '../types/EducationHub';
import { useGuides, useGuideCompletions, useGuideMetadata } from '../hooks/useEducationHub';
import { useNews, useNewsMetadata } from '../hooks/useNews';
import { useUpdates, useUpdateMetadata } from '../hooks/useUpdates';
import { useResources } from '../hooks/useResources';
import { useTemplates } from '../hooks/useTemplates';

interface EducationHubContextType {
  guides: Guide[];
  guidesLoading: boolean;
  guidesError: string | null;
  totalCount: number;
  hasMore: boolean;
  fetchGuides: ReturnType<typeof useGuides>["fetchGuides"];
  getGuideById: ReturnType<typeof useGuides>["getGuideById"];

  completions: GuideCompletion[];
  completionsLoading: boolean;
  completionsError: string | null;
  getUserCompletions: ReturnType<typeof useGuideCompletions>["getUserCompletions"];
  getGuideCompletion: ReturnType<typeof useGuideCompletions>["getGuideCompletion"];
  updateCompletion: ReturnType<typeof useGuideCompletions>["updateCompletion"];
  // getAgencyStats: ReturnType<typeof useGuideCompletions>["getAgencyStats"];

  // Updates
  updates: UpdateNote[];
  updatesLoading: boolean;
  updatesError: string | null;
  updatesTotalCount: number;
  updatesHasMore: boolean;
  fetchUpdates: ReturnType<typeof useUpdates>["fetchUpdates"];
  getUpdateById: ReturnType<typeof useUpdates>["getUpdateById"];
  getLatestUpdate: ReturnType<typeof useUpdates>["getLatestUpdate"];
  updateCategories: string[];
  updateTypes: string[];
  updateCategoriesLoading: boolean;
  updateCategoriesError: string | null;
  updateTypesLoading: boolean;
  updateTypesError: string | null;
  refetchUpdateCategories: ReturnType<typeof useUpdateMetadata>["fetchCategories"];
  refetchUpdateTypes: ReturnType<typeof useUpdateMetadata>["fetchTypes"];
  categories: string[];
  categoriesLoading: boolean;
  categoriesError: string | null;
  refetchCategories: ReturnType<typeof useGuideMetadata>["refetchCategories"];

  // News
  news: NewsPost[];
  newsLoading: boolean;
  newsError: string | null;
  newsTotalCount: number;
  newsHasMore: boolean;
  fetchNews: ReturnType<typeof useNews>["fetchNews"];
  getNewsById: ReturnType<typeof useNews>["getNewsById"];
  getFeaturedNews: ReturnType<typeof useNews>["getFeaturedNews"];

  newsCategories: string[];
  newsCategoriesLoading: boolean;
  newsCategoriesError: string | null;
  refetchNewsCategories: ReturnType<typeof useNewsMetadata>["fetchCategories"];
  
  // Resources
  resources: Resource[];
  resourcesLoading: boolean;
  resourcesError: string | null;
  fetchResources: ReturnType<typeof useResources>["fetchResources"];
  getResourceById: ReturnType<typeof useResources>["getResourceById"];
  getResourceCategories: ReturnType<typeof useResources>["getResourceCategories"];
  getResourceTags: ReturnType<typeof useResources>["getResourceTags"];
  downloadResource: ReturnType<typeof useResources>["downloadResource"];

  // Templates
  templates: DocumentTemplate[];
  templatesLoading: boolean;
  templatesError: string | null;
  fetchTemplates: ReturnType<typeof useTemplates>["fetchTemplates"];
  getTemplateById: ReturnType<typeof useTemplates>["getTemplateById"];
  getTemplateByDocumentType: ReturnType<typeof useTemplates>["getTemplateByDocumentType"];
  getTemplateCategories: ReturnType<typeof useTemplates>["getTemplateCategories"];
  downloadTemplate: ReturnType<typeof useTemplates>["downloadTemplate"];
  downloadTemplateByDocID: ReturnType<typeof useTemplates>["downloadTemplateByDocID"];
}

const EducationHubContext = createContext<EducationHubContextType | undefined>(undefined);

export const EducationHubProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Guides
  const {
    guides,
    loading: guidesLoading,
    error: guidesError,
    totalCount,
    hasMore,
    fetchGuides,
    getGuideById
  } = useGuides();

  // Completions
  const {
    completions,
    loading: completionsLoading,
    error: completionsError,
    getUserCompletions,
    getGuideCompletion,
    updateCompletion,
    // getAgencyStats
  } = useGuideCompletions();

  // Categories
  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
    refetchCategories
  } = useGuideMetadata();

  // News
  // Updates
  const {
    updates,
    loading: updatesLoading,
    error: updatesError,
    totalCount: updatesTotalCount,
    hasMore: updatesHasMore,
    fetchUpdates,
    getUpdateById,
    getLatestUpdate
  } = useUpdates();

  const {
    categories: updateCategories,
    types: updateTypes,
    loading: updateCategoriesLoading,
    error: updateCategoriesError,
    fetchCategories: refetchUpdateCategories,
    fetchTypes: refetchUpdateTypes
  } = useUpdateMetadata();
  const {
    news,
    loading: newsLoading,
    error: newsError,
    totalCount: newsTotalCount,
    hasMore: newsHasMore,
    fetchNews,
    getNewsById,
    getFeaturedNews
  } = useNews();

  // News Categories
  const {
    categories: newsCategories,
    loading: newsCategoriesLoading,
    error: newsCategoriesError,
    fetchCategories: refetchNewsCategories
  } = useNewsMetadata();

  // Resources
  const {
    resources,
    loading: resourcesLoading,
    error: resourcesError,
    fetchResources,
    getResourceById,
    getResourceCategories,
    getResourceTags,
    downloadResource
  } = useResources();

  // Templates
  const {
    templates,
    loading: templatesLoading,
    error: templatesError,
    fetchTemplates,
    getTemplateById,
    getTemplateByDocumentType,
    getTemplateCategories,
    downloadTemplate,
    downloadTemplateByDocID
  } = useTemplates();

  const value: EducationHubContextType = {
    guides,
    guidesLoading,
    guidesError,
    totalCount,
    hasMore,
    fetchGuides,
    getGuideById,
    completions,
    completionsLoading,
    completionsError,
    getUserCompletions,
    getGuideCompletion,
    // Updates
    updates,
    updatesLoading,
    updatesError,
    updatesTotalCount,
    updatesHasMore,
    fetchUpdates,
    getUpdateById,
    getLatestUpdate,
    updateCategories,
    updateTypes,
    updateCategoriesLoading,
    updateCategoriesError,
    updateTypesLoading: updateCategoriesLoading, // use same loading for both
    updateTypesError: updateCategoriesError, // use same error for both
    refetchUpdateCategories,
    refetchUpdateTypes,
    updateCompletion,
    // getAgencyStats,
    categories,
    categoriesLoading,
    categoriesError,
    refetchCategories,
    news,
    newsLoading,
    newsError,
    newsTotalCount,
    newsHasMore,
    fetchNews,
    getNewsById,
    getFeaturedNews,
    newsCategories,
    newsCategoriesLoading,
    newsCategoriesError,
    refetchNewsCategories,
    // Resources
    resources,
    resourcesLoading,
    resourcesError,
    fetchResources,
    getResourceById,
    getResourceCategories,
    getResourceTags,
    downloadResource,
    // Templates
    templates,
    templatesLoading,
    templatesError,
    fetchTemplates,
    getTemplateById,
    getTemplateByDocumentType,
    getTemplateCategories,
    downloadTemplate,
    downloadTemplateByDocID,
  };

  return (
    <EducationHubContext.Provider value={value}>
      {children}
    </EducationHubContext.Provider>
  );
};

export const useEducationHub = (): EducationHubContextType => {
  const context = useContext(EducationHubContext);
  if (!context) {
    throw new Error('useEducationHub must be used within an EducationHubProvider');
  }
  return context;
};
